import { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import toast from "react-hot-toast";
import { 
    useAdminUsers, 
    useCreateUser, 
    useUpdateUser,
    useDeleteUser,
    useResetPasswordDefault,
    useGetConferenceAccess,
    useUpdateConferenceAccess,
} from "@/services/adminUserService";
import { loginAs } from "@/services/auth";
import { useAuth } from "@/hooks/useAuth";
import { useQueryClient, useQuery } from "@tanstack/react-query";
import { useAdminRoles } from "@/services/adminRoleService";
import { useAdminParticipants } from "@/services/adminParticipantService";
import { useEventGuests } from "@/services/eventGuestService";
import type { User, UserPayload } from "@/services/adminUserService";
import { useSendAccountNotification, useNotificationEligibleCount } from "@/services/adminUserNotificationService";

import { Button } from "@/components/ui/button";
import {
    Form,
    FormControl,
    FormField,
    FormItem,
    FormLabel,
    FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog";
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { axiosClient } from "@/lib/axiosClient";
import { Edit2, Plus, Trash2, Users, Loader2, KeyRound, Search, LogIn, ShieldCheck, Send, MailCheck, Check, ChevronsUpDown } from "lucide-react";
import {
    Command,
    CommandEmpty,
    CommandGroup,
    CommandInput,
    CommandItem,
    CommandList,
} from "@/components/ui/command";
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from "@/components/ui/popover";
import { cn } from "@/lib/utils";

// Schema Validation
const formSchema = z.object({
    name: z.string().min(1, "Nama lengkap wajib diisi"),
    email: z.string().email("Email tidak valid"),
    niu: z.string().optional().nullable(),
    nip: z.string().optional().nullable(),
    password: z.string().optional(),
    role_id: z.string().min(1, "Role wajib dipilih"),
    relation_type: z.enum(["none", "participant", "event_guest"]).default("none"),
    participant_id: z.string().optional().nullable(),
    event_guest_id: z.string().optional().nullable(),
    is_active: z.boolean().default(true),
});
type FormValues = z.infer<typeof formSchema>;

export default function UsersPage() {
    const { setAuthSuccess } = useAuth();
    const queryClient = useQueryClient();
    
    const { data: users, isLoading } = useAdminUsers();
    const { data: roles } = useAdminRoles();
    const [searchParticipantText, setSearchParticipantText] = useState("");
    const [debouncedParticipantSearch, setDebouncedParticipantSearch] = useState("");
    
    useEffect(() => {
        const handler = setTimeout(() => {
            setDebouncedParticipantSearch(searchParticipantText);
        }, 500);
        return () => clearTimeout(handler);
    }, [searchParticipantText]);

    const { data: participantsData, isLoading: isLoadingParticipants } = useAdminParticipants(1, debouncedParticipantSearch, 50);
    const participants = participantsData?.data || [];
    
    const [searchGuestText, setSearchGuestText] = useState("");
    const [debouncedGuestSearch, setDebouncedGuestSearch] = useState("");
    
    useEffect(() => {
        const handler = setTimeout(() => {
            setDebouncedGuestSearch(searchGuestText);
        }, 500);
        return () => clearTimeout(handler);
    }, [searchGuestText]);

    const { data: guestsData, isLoading: isLoadingGuests } = useEventGuests(1, debouncedGuestSearch, 50);
    const guests = guestsData?.data || [];
    
    const createMutation = useCreateUser();
    const updateMutation = useUpdateUser();
    const deleteMutation = useDeleteUser();
    const resetPasswordMutation = useResetPasswordDefault();

    const isMutating = createMutation.isPending || updateMutation.isPending;

    // Client-side pagination and search state
    const [page, setPage] = useState(1);
    const [perPage, setPerPage] = useState(10);
    const [search, setSearch] = useState("");
    const [roleFilter, setRoleFilter] = useState("all");
    const [isAccessModalOpen, setIsAccessModalOpen] = useState(false);
    const [selectedUserId, setSelectedUserId] = useState<number | null>(null);
    const [selectedConferences, setSelectedConferences] = useState<number[]>([]);
    const [openParticipant, setOpenParticipant] = useState(false);
    const [openGuest, setOpenGuest] = useState(false);

    const { data: conferences } = useQuery({ queryKey: ["adminConferences2"], queryFn: async () => { const { data } = await axiosClient.get("/conference-billing/conferences", { withCredentials: true }); return data?.data || data; } });
    const { data: userAccess, isFetching: isFetchingAccess } = useGetConferenceAccess(selectedUserId);
    const updateAccess = useUpdateConferenceAccess();

    // --- Kirim Notifikasi (Kirim Akun ke Email) ---
    const [isSendNotifOpen, setIsSendNotifOpen] = useState(false);
    const [notifRoleId, setNotifRoleId] = useState<string>("");
    const [notifType, setNotifType] = useState<string>("account_credentials");
    const [notifDefaultPassword, setNotifDefaultPassword] = useState("");
    const [notifSelectedUserIds, setNotifSelectedUserIds] = useState<number[]>([]);
    const [notifUserSearch, setNotifUserSearch] = useState("");
    const [notifFilter, setNotifFilter] = useState<string>("never_sent");
    const [notifResult, setNotifResult] = useState<{ sent: number; failed: number; total_targeted: number } | null>(null);

    const sendAccountNotification = useSendAccountNotification();
    const eligibleCount = useNotificationEligibleCount(
        isSendNotifOpen && notifRoleId && notifSelectedUserIds.length === 0
            ? { role_id: Number(notifRoleId), notif_type: notifType as "account_credentials", filter: notifFilter as "never_sent" | "all" }
            : null
    );

    const notifRoleUsers = (users || []).filter((u) => notifRoleId && String(u.role_id) === notifRoleId);
    const notifRoleUsersFiltered = notifRoleUsers.filter((u) =>
        !notifUserSearch ||
        u.name.toLowerCase().includes(notifUserSearch.toLowerCase()) ||
        u.email.toLowerCase().includes(notifUserSearch.toLowerCase())
    );

    const resetNotifForm = () => {
        setNotifRoleId("");
        setNotifType("account_credentials");
        setNotifDefaultPassword("");
        setNotifSelectedUserIds([]);
        setNotifUserSearch("");
        setNotifFilter("never_sent");
        setNotifResult(null);
    };

    const handleOpenSendNotif = () => {
        resetNotifForm();
        setIsSendNotifOpen(true);
    };

    const handleSubmitSendNotif = () => {
        if (!notifRoleId) {
            toast.error("Pilih role penerima terlebih dahulu");
            return;
        }
        if (notifType === "account_credentials" && !notifDefaultPassword) {
            toast.error("Password default wajib diisi");
            return;
        }

        const roleName = roles?.find((r) => String(r.role_id) === notifRoleId)?.role_name || "role ini";
        const targetDescription = notifSelectedUserIds.length > 0
            ? `${notifSelectedUserIds.length} pengguna terpilih`
            : `${eligibleCount.data ?? "?"} pengguna (${notifFilter === "never_sent" ? "belum pernah dikirimi" : "semua"})`;

        setAlertConfig({
            title: "Kirim Notifikasi Akun",
            description: `Anda akan mengirim email akun (password akan DIRESET ke password default yang diinput) ke ${targetDescription} dengan role "${roleName}". Tindakan ini tidak dapat dibatalkan. Lanjutkan?`,
            actionText: "Ya, Kirim",
            isDestructive: false,
            action: () => {
                sendAccountNotification.mutate(
                    {
                        role_id: Number(notifRoleId),
                        notif_type: notifType as "account_credentials",
                        default_password: notifDefaultPassword,
                        user_ids: notifSelectedUserIds.length > 0 ? notifSelectedUserIds : undefined,
                        filter: notifFilter as "never_sent" | "all",
                    },
                    {
                        onSuccess: (data) => {
                            toast.success(data.message);
                            setNotifResult({ sent: data.sent, failed: data.failed, total_targeted: data.total_targeted });
                        },
                        onError: (error: any) => {
                            toast.error(error?.response?.data?.message || "Gagal mengirim notifikasi");
                        },
                    }
                );
            }
        });
        setIsAlertOpen(true);
    };

    useEffect(() => {
        if (userAccess) {
            setSelectedConferences(userAccess);
        }
    }, [userAccess]);

    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const [editingUser, setEditingUser] = useState<User | null>(null);

    // Alert dialog state
    const [isAlertOpen, setIsAlertOpen] = useState(false);
    const [alertConfig, setAlertConfig] = useState<{
        title: string;
        description: string;
        actionText: string;
        action: () => void;
        isDestructive?: boolean;
    } | null>(null);

    // Client-side filtering and pagination logic
    const filteredUsers = (users || []).filter(user => 
        ((user.name || "").toLowerCase().includes(search.toLowerCase()) || 
        (user.email || "").toLowerCase().includes(search.toLowerCase()) ||
        (user.role?.role_name || "").toLowerCase().includes(search.toLowerCase())) &&
        (roleFilter === "all" || String(user.role_id) === roleFilter)
    );
    const totalItems = filteredUsers.length;
    const totalPages = Math.ceil(totalItems / perPage) || 1;
    const paginatedUsers = filteredUsers.slice((page - 1) * perPage, page * perPage);
    const fromItem = totalItems === 0 ? 0 : (page - 1) * perPage + 1;
    const toItem = Math.min(page * perPage, totalItems);

    const activeUsersCount = (users || []).filter(u => u.is_active).length;
    const inactiveUsersCount = (users || []).filter(u => !u.is_active).length;

    const form = useForm<FormValues>({
        resolver: zodResolver(formSchema),
        defaultValues: {
            name: "",
            email: "",
            niu: "",
            password: "",
            role_id: "",
            is_active: true,
        },
    });

    const watchRelationType = form.watch("relation_type");

    const handleOpenDialog = (user?: User) => {
        if (user) {
            setEditingUser(user);
            let relationType: "none" | "participant" | "event_guest" = "none";
            if (user.participant_id) relationType = "participant";
            else if (user.event_guest_id) relationType = "event_guest";

            form.reset({
                name: user.name,
                email: user.email,
                niu: user.niu || "",
                password: "",
                role_id: user.role_id ? user.role_id.toString() : "",
                relation_type: relationType,
                participant_id: user.participant_id ? user.participant_id.toString() : "0",
                event_guest_id: user.event_guest_id ? user.event_guest_id.toString() : "0",
                is_active: Boolean(user.is_active),
            });
        } else {
            setEditingUser(null);
            form.reset({
                name: "",
                email: "",
                niu: "",
                password: "",
                role_id: "",
                relation_type: "none",
                participant_id: "0",
                event_guest_id: "0",
                is_active: true,
            });
        }
        setIsDialogOpen(true);
    };

    const onSubmit = (values: FormValues) => {
        const payload: UserPayload = {
            name: values.name,
            email: values.email,
            niu: values.niu ?? null,
            nip: values.nip ?? null,
            role_id: parseInt(values.role_id),
            is_active: values.is_active,
            participant_id: values.relation_type === "participant" && values.participant_id && values.participant_id !== "0" ? Number(values.participant_id) : null,
            event_guest_id: values.relation_type === "event_guest" && values.event_guest_id && values.event_guest_id !== "0" ? Number(values.event_guest_id) : null,
        };

        if (values.password) {
            payload.password = values.password;
        }

        if (editingUser) {
            updateMutation.mutate(
                { id: editingUser.user_id, payload },
                {
                    onSuccess: () => {
                        toast.success("Pengguna berhasil diperbarui");
                        setIsDialogOpen(false);
                    },
                    onError: (error: any) => {
                        toast.error(error?.response?.data?.message || "Gagal memperbarui pengguna");
                    },
                }
            );
        } else {
            if (!values.password) {
                form.setError("password", { message: "Password wajib diisi untuk pengguna baru" });
                return;
            }
            createMutation.mutate(payload, {
                onSuccess: () => {
                    toast.success("Pengguna berhasil ditambahkan");
                    setIsDialogOpen(false);
                },
                onError: (error: any) => {
                    toast.error(error?.response?.data?.message || "Gagal menambahkan pengguna");
                },
            });
        }
    };

    const handleDelete = (id: number) => {
        setAlertConfig({
            title: "Hapus Pengguna",
            description: "Apakah Anda yakin ingin menghapus pengguna ini? Tindakan ini tidak dapat dibatalkan.",
            actionText: "Hapus",
            isDestructive: true,
            action: () => {
                deleteMutation.mutate(id, {
                    onSuccess: () => toast.success("Pengguna berhasil dihapus"),
                    onError: (error: any) => toast.error(error?.response?.data?.message || "Gagal menghapus pengguna"),
                });
            }
        });
        setIsAlertOpen(true);
    };

    const handleManageAccess = (user: User) => {
        setSelectedUserId(user.user_id);
        setIsAccessModalOpen(true);
    };

    const handleSaveAccess = async () => {
        if (!selectedUserId) return;
        updateAccess.mutate({ id: selectedUserId, conference_ids: selectedConferences }, {
            onSuccess: () => {
                toast.success("Akses berhasil diperbarui!");
                setIsAccessModalOpen(false);
            },
            onError: () => toast.error("Gagal memperbarui akses")
        });
    };

    const handleResetPassword = (id: number) => {
        setAlertConfig({
            title: "Reset Password",
            description: "Apakah Anda yakin ingin mereset password pengguna ini ke default ('password')?",
            actionText: "Reset",
            isDestructive: false,
            action: () => {
                resetPasswordMutation.mutate(id, {
                    onSuccess: () => toast.success("Password berhasil direset"),
                    onError: (error: any) => toast.error(error?.response?.data?.message || "Gagal mereset password"),
                });
            }
        });
        setIsAlertOpen(true);
    };

    const handleLoginAs = async (userId: number) => {
        setAlertConfig({
            title: "Login As",
            description: "Anda akan login sebagai pengguna ini. Apakah Anda yakin?",
            actionText: "Ya, Login",
            isDestructive: false,
            action: async () => {
                const toastId = toast.loading("Sedang login...");
                try {
                    const res = await loginAs(userId);
                    toast.success("Berhasil login sebagai " + res.user.name, { id: toastId });
                    
                    setAuthSuccess(res.user, res.access_token);
                    queryClient.clear(); // Bersihkan cache lama
                    
                    window.location.href = "/";
                } catch (error: any) {
                    toast.error(error?.response?.data?.message || "Gagal melakukan Login As", { id: toastId });
                }
            }
        });
        setIsAlertOpen(true);
    };

    if (isLoading) {
        return (
            <div className="flex items-center justify-center h-[50vh]">
                <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
        );
    }

    return (
        <div className="max-w-7xl mx-auto space-y-8 pb-20 px-4 md:px-0">
            {/* --- HEADER --- */}
            <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 bg-white dark:bg-slate-900 p-6 md:p-8 rounded-3xl shadow-sm border border-slate-100 dark:border-slate-800">
                <div>
                    <div className="flex items-center gap-3 mb-2">
                        <div className="p-2.5 rounded-xl bg-indigo-50 text-indigo-600 dark:bg-indigo-900/30 dark:text-indigo-400">
                            <Users className="w-6 h-6" />
                        </div>
                        <h1 className="text-2xl font-black text-slate-900 dark:text-white tracking-tight">Data Pengguna</h1>
                    </div>
                    <p className="text-sm text-slate-500 dark:text-slate-400 max-w-xl">
                        Manajemen data pengguna dan hak akses (role).
                    </p>
                </div>
                <div className="flex items-center gap-3 shrink-0 flex-wrap">
                    <Button onClick={handleOpenSendNotif} variant="outline" className="rounded-xl h-11 px-6 font-bold border-slate-200 dark:border-slate-700 hover:bg-slate-100 dark:hover:bg-slate-800 transition-all">
                        <Send className="w-4 h-4 mr-2" /> Kirim Notifikasi
                    </Button>
                    <Button onClick={() => handleOpenDialog()} className="bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl h-11 px-6 font-bold shadow-md transition-all">
                        <Plus className="w-4 h-4 mr-2" /> Tambah Pengguna
                    </Button>
                </div>
            </div>

            {/* --- SUMMARY CARDS --- */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="bg-white dark:bg-slate-900 p-5 rounded-2xl border border-slate-100 dark:border-slate-800 shadow-sm flex flex-col justify-between h-[100px] relative overflow-hidden group">
                    <div className="absolute top-0 right-0 p-4 opacity-10 group-hover:opacity-20 transition-opacity">
                        <Users className="w-12 h-12 text-indigo-500" />
                    </div>
                    <p className="text-[11px] font-bold text-slate-500 dark:text-slate-400 uppercase tracking-widest flex items-center gap-1.5"><Users className="w-4 h-4 text-indigo-500" /> Total Pengguna</p>
                    <p className="text-3xl font-black text-slate-900 dark:text-white">{(users || []).length}</p>
                </div>
                <div className="bg-white dark:bg-slate-900 p-5 rounded-2xl border border-slate-100 dark:border-slate-800 shadow-sm flex flex-col justify-between h-[100px] relative overflow-hidden group">
                    <div className="absolute top-0 right-0 p-4 opacity-10 group-hover:opacity-20 transition-opacity">
                        <Users className="w-12 h-12 text-emerald-500" />
                    </div>
                    <p className="text-[11px] font-bold text-slate-500 dark:text-slate-400 uppercase tracking-widest flex items-center gap-1.5"><div className="w-2 h-2 rounded-full bg-emerald-500"></div> Aktif</p>
                    <p className="text-3xl font-black text-slate-900 dark:text-white">{activeUsersCount}</p>
                </div>
                <div className="bg-white dark:bg-slate-900 p-5 rounded-2xl border border-slate-100 dark:border-slate-800 shadow-sm flex flex-col justify-between h-[100px] relative overflow-hidden group">
                    <div className="absolute top-0 right-0 p-4 opacity-10 group-hover:opacity-20 transition-opacity">
                        <Users className="w-12 h-12 text-rose-500" />
                    </div>
                    <p className="text-[11px] font-bold text-slate-500 dark:text-slate-400 uppercase tracking-widest flex items-center gap-1.5"><div className="w-2 h-2 rounded-full bg-rose-500"></div> Nonaktif</p>
                    <p className="text-3xl font-black text-slate-900 dark:text-white">{inactiveUsersCount}</p>
                </div>
            </div>

            {/* --- TOOLBAR (FILTER & SEARCH) --- */}
            <div className="flex flex-col gap-4 w-full">
                <div className="flex flex-wrap items-center gap-3 w-full">
                    <div className="relative flex-1 min-w-[250px]">
                        <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                        <input
                            type="text"
                            placeholder="Cari nama, email, role..."
                            value={search}
                            onChange={(e) => { setSearch(e.target.value); setPage(1); }}
                            className="w-full h-11 pl-11 pr-4 py-2 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl text-sm focus:ring-2 focus:ring-indigo-500 outline-none shadow-sm transition-all font-medium"
                        />
                    </div>
                    <Select value={roleFilter} onValueChange={(val) => { setRoleFilter(val); setPage(1); }}>
                        <SelectTrigger className="h-11 bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800 rounded-2xl w-full sm:w-48 text-sm font-medium">
                            <SelectValue placeholder="Semua Role" />
                        </SelectTrigger>
                        <SelectContent className="rounded-xl">
                            <SelectItem value="all">Semua Role</SelectItem>
                            {roles?.filter((r) => r.is_active).map((role) => (
                                <SelectItem key={role.role_id} value={String(role.role_id)}>
                                    {role.role_name}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </div>
            </div>

            {/* --- DATA TABLE --- */}
            <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-sm border border-slate-200 dark:border-slate-800 overflow-hidden relative">
                    <div className="overflow-x-auto">
                        <table className="w-full text-left text-sm whitespace-nowrap">
                            <thead className="bg-slate-50 dark:bg-slate-800/50 text-slate-500 dark:text-slate-400 font-bold uppercase tracking-wider text-[11px]">
                                <tr>
                                    <th className="px-6 py-5 border-b border-slate-100 dark:border-slate-800 w-[50px] text-center">No</th>
                                    <th className="px-6 py-5 border-b border-slate-100 dark:border-slate-800">Nama</th>
                                    <th className="px-6 py-5 border-b border-slate-100 dark:border-slate-800">Email</th>
                                    <th className="px-6 py-5 border-b border-slate-100 dark:border-slate-800">Role</th>
                                    <th className="px-6 py-5 border-b border-slate-100 dark:border-slate-800 text-center">Status</th>
                                    <th className="px-6 py-5 border-b border-slate-100 dark:border-slate-800 text-right sticky right-0 z-10 bg-slate-50 dark:bg-slate-800/90 shadow-[-10px_0_15px_-10px_rgba(0,0,0,0.1)]">Aksi</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                                {!paginatedUsers || paginatedUsers.length === 0 ? (
                                    <tr>
                                        <td colSpan={6} className="p-12 text-center text-slate-500">
                                            Belum ada data pengguna.
                                        </td>
                                    </tr>
                                ) : (
                                    paginatedUsers.map((user, index) => (
                                        <tr key={user.user_id} className="group hover:bg-slate-50/50 dark:hover:bg-slate-800/20 transition-colors bg-white dark:bg-slate-900">
                                            <td className="px-6 py-4 text-center font-medium">{(page - 1) * perPage + index + 1}</td>
                                            <td className="px-6 py-4 font-semibold text-slate-900 dark:text-slate-100">
                                                {user.name}
                                            </td>
                                            <td className="px-6 py-4 text-slate-500">
                                                {user.email}
                                            </td>
                                            <td className="px-6 py-4">
                                                {user.role ? (
                                                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-md text-[11px] font-bold bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300">
                                                        {user.role.role_name}
                                                    </span>
                                                ) : (
                                                    "-"
                                                )}
                                            </td>
                                            <td className="px-6 py-4 text-center">
                                                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                                    user.is_active 
                                                        ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300' 
                                                        : 'bg-slate-100 text-slate-800 dark:bg-slate-800 dark:text-slate-300'
                                                }`}>
                                                    {user.is_active ? 'Aktif' : 'Nonaktif'}
                                                </span>
                                            </td>
                                            <td className="px-6 py-4 text-right sticky right-0 z-10 bg-white dark:bg-slate-900 group-hover:bg-slate-50/50 dark:group-hover:bg-slate-800/80 shadow-[-10px_0_15px_-10px_rgba(0,0,0,0.1)] transition-colors">
                                                <div className="flex justify-end gap-2">
                                                    <Button
                                                        variant="outline"
                                                        size="icon"
                                                        title="Kelola Akses Track (Role 5)"
                                                        className="h-8 w-8 text-indigo-600 hover:text-indigo-700 hover:bg-indigo-50 dark:hover:bg-indigo-900/20 rounded-lg"
                                                        onClick={() => handleManageAccess(user)}
                                                    >
                                                        <ShieldCheck className="h-4 w-4" />
                                                    </Button>
                                                    <Button
                                                        variant="outline"
                                                        size="icon"
                                                        title="Reset Password Default"
                                                        className="h-8 w-8 text-amber-600 hover:text-amber-700 hover:bg-amber-50 dark:hover:bg-amber-900/20 rounded-lg"
                                                        onClick={() => handleResetPassword(user.user_id)}
                                                    >
                                                        <KeyRound className="h-4 w-4" />
                                                    </Button>
                                                    <Button
                                                        variant="outline"
                                                        size="icon"
                                                        title="Login As"
                                                        className="h-8 w-8 text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50 dark:hover:bg-emerald-900/20 rounded-lg"
                                                        onClick={() => handleLoginAs(user.user_id)}
                                                    >
                                                        <LogIn className="h-4 w-4" />
                                                    </Button>
                                                    <Button
                                                        variant="outline"
                                                        size="icon"
                                                        title="Edit"
                                                        className="h-8 w-8 text-indigo-600 hover:text-indigo-700 hover:bg-indigo-50 dark:hover:bg-indigo-900/20 rounded-lg"
                                                        onClick={() => handleOpenDialog(user)}
                                                    >
                                                        <Edit2 className="h-4 w-4" />
                                                    </Button>
                                                    <Button
                                                        variant="outline"
                                                        size="icon"
                                                        title="Hapus"
                                                        className="h-8 w-8 text-rose-600 hover:text-rose-700 hover:bg-rose-50 dark:hover:bg-rose-900/20 rounded-lg"
                                                        onClick={() => handleDelete(user.user_id)}
                                                    >
                                                        <Trash2 className="h-4 w-4" />
                                                    </Button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>

                    {totalItems > 0 && (
                        <div className="p-4 md:p-5 border-t border-slate-100 dark:border-slate-800 bg-slate-50/50 dark:bg-slate-900/50 flex flex-col sm:flex-row items-center justify-between gap-4 text-sm">
                            <div className="flex items-center gap-3 text-slate-500 font-medium">
                                <span>Menampilkan {fromItem} - {toItem} dari {totalItems} data</span>
                                <div className="h-4 w-px bg-slate-300 dark:bg-slate-700 hidden sm:block"></div>
                                <div className="flex items-center gap-2">
                                    <label htmlFor="perPage">Tampilkan:</label>
                                    <select
                                        id="perPage"
                                        value={perPage}
                                        onChange={(e) => { setPerPage(Number(e.target.value)); setPage(1); }}
                                        className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-md px-2 py-1 outline-none focus:ring-2 focus:ring-primary font-bold"
                                    >
                                        <option value="10">10</option>
                                        <option value="25">25</option>
                                        <option value="50">50</option>
                                        <option value="100">100</option>
                                    </select>
                                </div>
                            </div>
                            
                            {totalPages > 1 && (
                                <div className="flex items-center gap-2">
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={() => setPage(p => Math.max(1, p - 1))}
                                        disabled={page === 1}
                                        className="rounded-lg"
                                    >
                                        Sebelumnya
                                    </Button>
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                                        disabled={page === totalPages}
                                        className="rounded-lg"
                                    >
                                        Selanjutnya
                                    </Button>
                                </div>
                            )}
                        </div>
                    )}
            </div>

            {/* Modal Form */}
            <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
                <DialogContent className="max-w-md bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800 rounded-3xl p-6 shadow-2xl">
                    <DialogHeader className="mb-4">
                        <DialogTitle className="text-xl font-bold text-slate-900 dark:text-white">
                            {editingUser ? "Edit Pengguna" : "Tambah Pengguna"}
                        </DialogTitle>
                    </DialogHeader>

                    <Form {...form}>
                        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
                            <FormField
                                control={form.control}
                                name="name"
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel className="text-slate-700 dark:text-slate-300 font-semibold">Nama Lengkap</FormLabel>
                                        <FormControl>
                                            <Input placeholder="John Doe" {...field} className="h-11 rounded-xl bg-slate-50 dark:bg-slate-900/50 border-slate-200 dark:border-slate-800 focus-visible:ring-indigo-500" />
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />

                            <FormField
                                control={form.control}
                                name="email"
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel className="text-slate-700 dark:text-slate-300 font-semibold">Email</FormLabel>
                                        <FormControl>
                                            <Input type="email" placeholder="john@example.com" {...field} className="h-11 rounded-xl bg-slate-50 dark:bg-slate-900/50 border-slate-200 dark:border-slate-800 focus-visible:ring-indigo-500" />
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />

                            <FormField
                                control={form.control}
                                name="niu"
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel className="text-slate-700 dark:text-slate-300 font-semibold">NIU / NIP (Opsional)</FormLabel>
                                        <FormControl>
                                            <Input placeholder="Masukkan NIU / NIP" {...field} value={field.value || ''} className="h-11 rounded-xl bg-slate-50 dark:bg-slate-900/50 border-slate-200 dark:border-slate-800 focus-visible:ring-indigo-500" />
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />

                            <FormField
                                control={form.control}
                                name="password"
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel className="text-slate-700 dark:text-slate-300 font-semibold">
                                            Password {editingUser && "(Opsional)"}
                                        </FormLabel>
                                        <FormControl>
                                            <Input type="password" placeholder="******" {...field} className="h-11 rounded-xl bg-slate-50 dark:bg-slate-900/50 border-slate-200 dark:border-slate-800 focus-visible:ring-indigo-500" />
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />

                            <FormField
                                control={form.control}
                                name="role_id"
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel className="text-slate-700 dark:text-slate-300 font-semibold">Role</FormLabel>
                                        <Select onValueChange={field.onChange} value={field.value}>
                                            <FormControl>
                                                <SelectTrigger className="h-11 rounded-xl bg-slate-50 dark:bg-slate-900/50 border-slate-200 dark:border-slate-800 focus:ring-indigo-500">
                                                    <SelectValue placeholder="Pilih role" />
                                                </SelectTrigger>
                                            </FormControl>
                                            <SelectContent className="rounded-xl border-slate-200 dark:border-slate-800 shadow-xl">
                                                {roles?.filter((r) => r.is_active).map((role) => (
                                                    <SelectItem key={role.role_id} value={String(role.role_id)} className="focus:bg-indigo-50 dark:focus:bg-indigo-900/30">
                                                        {role.role_name}
                                                    </SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />

                            {/* SUMBER DATA / RELATION TYPE */}
                            <FormField
                                control={form.control}
                                name="relation_type"
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel className="text-slate-700 dark:text-slate-300 font-semibold">Tipe Relasi Profil (Opsional)</FormLabel>
                                        <Select 
                                            onValueChange={(val) => {
                                                field.onChange(val);
                                                if (val !== "participant") form.setValue("participant_id", "0");
                                                if (val !== "event_guest") form.setValue("event_guest_id", "0");
                                            }} 
                                            value={field.value}
                                        >
                                            <FormControl>
                                                <SelectTrigger className="h-11 rounded-xl bg-slate-50 dark:bg-slate-900/50 border-slate-200 dark:border-slate-800 focus:ring-indigo-500">
                                                    <SelectValue placeholder="Pilih Tipe Relasi" />
                                                </SelectTrigger>
                                            </FormControl>
                                            <SelectContent className="rounded-xl border-slate-200 dark:border-slate-800 shadow-xl">
                                                <SelectItem value="none" className="focus:bg-indigo-50 dark:focus:bg-indigo-900/30">-- Tidak Ada Relasi --</SelectItem>
                                                <SelectItem value="participant" className="focus:bg-indigo-50 dark:focus:bg-indigo-900/30">Peserta (Participant)</SelectItem>
                                                <SelectItem value="event_guest" className="focus:bg-indigo-50 dark:focus:bg-indigo-900/30">Tamu / Undangan (Event Guest)</SelectItem>
                                            </SelectContent>
                                        </Select>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />

                            {/* PARTICIPANT_ID DROPDOWN */}
                            {watchRelationType === "participant" && (
                                <FormField
                                    control={form.control}
                                    name="participant_id"
                                    render={({ field }) => (
                                        <FormItem className="flex flex-col">
                                            <FormLabel className="text-slate-700 dark:text-slate-300 font-semibold mb-1">Pilih Data Peserta</FormLabel>
                                            <Popover modal={true} open={openParticipant} onOpenChange={setOpenParticipant}>
                                                <PopoverTrigger asChild>
                                                    <Button
                                                        type="button"
                                                        variant="outline"
                                                        role="combobox"
                                                        aria-expanded={openParticipant}
                                                        className={cn(
                                                            "w-full h-11 justify-between rounded-xl bg-slate-50 dark:bg-slate-900/50 border-slate-200 dark:border-slate-800 focus:ring-indigo-500",
                                                            !field.value && "text-muted-foreground"
                                                        )}
                                                    >
                                                        {field.value && field.value !== "0"
                                                            ? (() => {
                                                                const p = participants?.find(
                                                                    (pt: any) => String(pt.participant_id) === field.value
                                                                );
                                                                return p ? `${p.full_name} ${p.email ? `(${p.email})` : ''}` : "Pilih Peserta";
                                                            })()
                                                            : "Cari Peserta..."}
                                                        <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                                                    </Button>
                                                </PopoverTrigger>
                                                <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0 rounded-xl border-slate-200 dark:border-slate-800 shadow-xl max-h-60 z-[100]" align="start">
                                                    <Command shouldFilter={false}>
                                                        <CommandInput 
                                                            placeholder="Ketik nama atau email peserta..." 
                                                            value={searchParticipantText}
                                                            onValueChange={setSearchParticipantText}
                                                        />
                                                        <CommandList>
                                                            <CommandEmpty>
                                                                {isLoadingParticipants ? "Mencari..." : "Peserta tidak ditemukan."}
                                                            </CommandEmpty>
                                                            <CommandGroup>
                                                                <CommandItem
                                                                    value="0"
                                                                    onSelect={() => {
                                                                        field.onChange("0");
                                                                        setOpenParticipant(false);
                                                                    }}
                                                                >
                                                                    <Check
                                                                        className={cn(
                                                                            "mr-2 h-4 w-4",
                                                                            field.value === "0" || !field.value ? "opacity-100" : "opacity-0"
                                                                        )}
                                                                    />
                                                                    <span className="text-slate-400 italic">-- Kosongkan --</span>
                                                                </CommandItem>
                                                                {participants?.map((p: any) => (
                                                                    <CommandItem
                                                                        value={`${p.full_name} ${p.email}`}
                                                                        key={p.participant_id}
                                                                        onSelect={() => {
                                                                            field.onChange(String(p.participant_id));
                                                                            setOpenParticipant(false);
                                                                        }}
                                                                    >
                                                                        <Check
                                                                            className={cn(
                                                                                "mr-2 h-4 w-4",
                                                                                String(p.participant_id) === field.value
                                                                                    ? "opacity-100"
                                                                                    : "opacity-0"
                                                                            )}
                                                                        />
                                                                        {p.full_name} {p.email ? `(${p.email})` : ''}
                                                                    </CommandItem>
                                                                ))}
                                                            </CommandGroup>
                                                        </CommandList>
                                                    </Command>
                                                </PopoverContent>
                                            </Popover>
                                            <FormMessage />
                                        </FormItem>
                                    )}
                                />
                            )}

                            {/* EVENT_GUEST DROPDOWN */}
                            {watchRelationType === "event_guest" && (
                                <FormField
                                    control={form.control}
                                    name="event_guest_id"
                                    render={({ field }) => (
                                        <FormItem className="flex flex-col">
                                            <FormLabel className="text-slate-700 dark:text-slate-300 font-semibold mb-1">Pilih Data Tamu / Undangan</FormLabel>
                                            <Popover modal={true} open={openGuest} onOpenChange={setOpenGuest}>
                                                <PopoverTrigger asChild>
                                                    <Button
                                                        type="button"
                                                        variant="outline"
                                                        role="combobox"
                                                        aria-expanded={openGuest}
                                                        className={cn(
                                                            "w-full h-11 justify-between rounded-xl bg-slate-50 dark:bg-slate-900/50 border-slate-200 dark:border-slate-800 focus:ring-indigo-500",
                                                            !field.value && "text-muted-foreground"
                                                        )}
                                                    >
                                                        {field.value && field.value !== "0"
                                                            ? (() => {
                                                                const g = guests?.find(
                                                                    (gt: any) => String(gt.id) === field.value
                                                                );
                                                                return g ? `${g.full_name} ${g.email ? `(${g.email})` : ''}` : "Pilih Tamu";
                                                            })()
                                                            : "Cari Tamu / Undangan..."}
                                                        <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                                                    </Button>
                                                </PopoverTrigger>
                                                <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0 rounded-xl border-slate-200 dark:border-slate-800 shadow-xl max-h-60 z-[100]" align="start">
                                                    <Command shouldFilter={false}>
                                                        <CommandInput 
                                                            placeholder="Ketik nama atau email tamu..." 
                                                            value={searchGuestText}
                                                            onValueChange={setSearchGuestText}
                                                        />
                                                        <CommandList>
                                                            <CommandEmpty>
                                                                {isLoadingGuests ? "Mencari..." : "Tamu / Undangan tidak ditemukan."}
                                                            </CommandEmpty>
                                                            <CommandGroup>
                                                                <CommandItem
                                                                    value="0"
                                                                    onSelect={() => {
                                                                        field.onChange("0");
                                                                        setOpenGuest(false);
                                                                    }}
                                                                >
                                                                    <Check
                                                                        className={cn(
                                                                            "mr-2 h-4 w-4",
                                                                            field.value === "0" || !field.value ? "opacity-100" : "opacity-0"
                                                                        )}
                                                                    />
                                                                    <span className="text-slate-400 italic">-- Kosongkan --</span>
                                                                </CommandItem>
                                                                {guests?.map((g: any) => (
                                                                    <CommandItem
                                                                        value={`${g.full_name} ${g.email}`}
                                                                        key={g.id}
                                                                        onSelect={() => {
                                                                            field.onChange(String(g.id));
                                                                            setOpenGuest(false);
                                                                        }}
                                                                    >
                                                                        <Check
                                                                            className={cn(
                                                                                "mr-2 h-4 w-4",
                                                                                String(g.id) === field.value
                                                                                    ? "opacity-100"
                                                                                    : "opacity-0"
                                                                        )}
                                                                        />
                                                                        {g.full_name} {g.email ? `(${g.email})` : ''}
                                                                    </CommandItem>
                                                                ))}
                                                            </CommandGroup>
                                                        </CommandList>
                                                    </Command>
                                                </PopoverContent>
                                            </Popover>
                                            <FormMessage />
                                        </FormItem>
                                    )}
                                />
                            )}

                            <FormField
                                control={form.control}
                                name="is_active"
                                render={({ field }) => (
                                    <FormItem className="flex flex-row items-center justify-between rounded-xl border border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-900/50 p-4">
                                        <div className="space-y-0.5">
                                            <FormLabel className="text-base text-slate-700 dark:text-slate-300 font-semibold">Status Aktif</FormLabel>
                                            <p className="text-xs text-slate-500 dark:text-slate-400">
                                                Apakah pengguna ini dapat login ke sistem?
                                            </p>
                                        </div>
                                        <FormControl>
                                            <Switch
                                                checked={field.value}
                                                onCheckedChange={field.onChange}
                                                className="data-[state=checked]:bg-indigo-600"
                                            />
                                        </FormControl>
                                    </FormItem>
                                )}
                            />

                            <div className="flex gap-3 pt-4 justify-end">
                                <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)} className="rounded-xl h-11 px-6 border-slate-200 dark:border-slate-700 hover:bg-slate-100 dark:hover:bg-slate-800">
                                    Batal
                                </Button>
                                <Button type="submit" disabled={isMutating} className="rounded-xl h-11 px-6 bg-indigo-600 hover:bg-indigo-700 text-white font-bold shadow-md">
                                    {isMutating && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                                    Simpan
                                </Button>
                            </div>
                        </form>
                    </Form>
                </DialogContent>
            </Dialog>

            {/* Custom Alert Dialog for Confirmations */}
            <AlertDialog open={isAlertOpen} onOpenChange={setIsAlertOpen}>
                <AlertDialogContent className="rounded-3xl border-slate-200 dark:border-slate-800 p-6 shadow-2xl max-w-sm">
                    <AlertDialogHeader>
                        <AlertDialogTitle className="text-xl font-bold text-slate-900 dark:text-white">
                            {alertConfig?.title}
                        </AlertDialogTitle>
                        <AlertDialogDescription className="text-slate-500 dark:text-slate-400">
                            {alertConfig?.description}
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter className="mt-6 gap-2">
                        <AlertDialogCancel className="rounded-xl h-11 border-slate-200 dark:border-slate-700 hover:bg-slate-100 dark:hover:bg-slate-800 font-medium">Batal</AlertDialogCancel>
                        <AlertDialogAction 
                            onClick={() => {
                                alertConfig?.action();
                                setIsAlertOpen(false);
                            }}
                            className={`rounded-xl h-11 font-bold text-white shadow-md ${
                                alertConfig?.isDestructive 
                                    ? "bg-rose-600 hover:bg-rose-700" 
                                    : "bg-indigo-600 hover:bg-indigo-700"
                            }`}
                        >
                            {alertConfig?.actionText}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        
            {/* Modal Akses Konferensi */}
            <Dialog open={isAccessModalOpen} onOpenChange={setIsAccessModalOpen}>
                <DialogContent className="sm:max-w-[500px] bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800 rounded-3xl overflow-hidden p-0">
                    <DialogHeader className="p-6 pb-4 border-b border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-900/50">
                        <DialogTitle className="text-xl font-bold flex items-center gap-3 text-slate-800 dark:text-white">
                            <ShieldCheck className="w-6 h-6 text-indigo-600 dark:text-indigo-500" />
                            Kelola Akses Track (Moderator)
                        </DialogTitle>
                    </DialogHeader>
                    <div className="p-6 max-h-[400px] overflow-y-auto space-y-4">
                        <p className="text-sm text-slate-500 mb-4">Pilih track mana saja yang dapat diakses oleh user ini.</p>
                        {isFetchingAccess ? (
                            <div className="flex justify-center p-8"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>
                        ) : (
                            conferences?.map((conf: any) => (
                                <div key={conf.id} className="flex items-center space-x-3 p-3 rounded-xl border border-slate-200 dark:border-slate-800 hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors">
                                    <Checkbox 
                                        id={`conf-${conf.id}`}
                                        checked={selectedConferences.includes(conf.id)}
                                        onCheckedChange={(checked) => {
                                            if (checked) {
                                                setSelectedConferences([...selectedConferences, conf.id]);
                                            } else {
                                                setSelectedConferences(selectedConferences.filter(id => id !== conf.id));
                                            }
                                        }}
                                        className="data-[state=checked]:bg-indigo-600 data-[state=checked]:border-indigo-600"
                                    />
                                    <label htmlFor={`conf-${conf.id}`} className="flex-1 cursor-pointer font-medium text-slate-700 dark:text-slate-300">
                                        {conf.name}
                                    </label>
                                </div>
                            ))
                        )}
                    </div>
                    <div className="flex p-6 pt-4 border-t justify-end gap-2 border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-900/50">
                        <Button type="button" variant="outline" onClick={() => setIsAccessModalOpen(false)} className="rounded-xl border-slate-300 dark:border-slate-700">
                            Batal
                        </Button>
                        <Button type="button" onClick={handleSaveAccess} disabled={updateAccess.isPending} className="rounded-xl bg-indigo-600 hover:bg-indigo-700 text-white shadow-lg">
                            {updateAccess.isPending ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : "Simpan Akses"}
                        </Button>
                    </div>
                </DialogContent>
            </Dialog>

            {/* Modal Kirim Notifikasi (Kirim Akun ke Email) */}
            <Dialog open={isSendNotifOpen} onOpenChange={setIsSendNotifOpen}>
                <DialogContent className="sm:max-w-[520px] bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800 rounded-3xl overflow-hidden p-0 max-h-[85vh] flex flex-col">
                    <DialogHeader className="p-6 pb-4 border-b border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-900/50 shrink-0">
                        <DialogTitle className="text-xl font-bold flex items-center gap-3 text-slate-800 dark:text-white">
                            <Send className="w-6 h-6 text-indigo-600 dark:text-indigo-500" />
                            Kirim Notifikasi ke Pengguna
                        </DialogTitle>
                    </DialogHeader>

                    <div className="p-6 space-y-5 overflow-y-auto">
                        {/* Jenis Role */}
                        <div className="space-y-2">
                            <label className="text-sm font-semibold text-slate-700 dark:text-slate-300">Jenis Role Penerima</label>
                            <Select value={notifRoleId} onValueChange={(v) => { setNotifRoleId(v); setNotifSelectedUserIds([]); }}>
                                <SelectTrigger className="h-11 rounded-xl bg-slate-50 dark:bg-slate-900/50 border-slate-200 dark:border-slate-800 focus:ring-indigo-500">
                                    <SelectValue placeholder="Pilih role" />
                                </SelectTrigger>
                                <SelectContent className="rounded-xl border-slate-200 dark:border-slate-800 shadow-xl">
                                    {roles?.filter((r) => r.is_active).map((role) => (
                                        <SelectItem key={role.role_id} value={String(role.role_id)}>
                                            {role.role_name}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>

                        {/* Jenis Notifikasi */}
                        <div className="space-y-2">
                            <label className="text-sm font-semibold text-slate-700 dark:text-slate-300">Jenis Notifikasi</label>
                            <Select value={notifType} onValueChange={setNotifType}>
                                <SelectTrigger className="h-11 rounded-xl bg-slate-50 dark:bg-slate-900/50 border-slate-200 dark:border-slate-800 focus:ring-indigo-500">
                                    <SelectValue placeholder="Pilih jenis notifikasi" />
                                </SelectTrigger>
                                <SelectContent className="rounded-xl border-slate-200 dark:border-slate-800 shadow-xl">
                                    <SelectItem value="account_credentials">Kirim Akun ke Email</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>

                        {/* Password Default - hanya untuk Kirim Akun ke Email */}
                        {notifType === "account_credentials" && (
                            <div className="space-y-2">
                                <label className="text-sm font-semibold text-slate-700 dark:text-slate-300">Password Default</label>
                                <Input
                                    type="text"
                                    placeholder="Password yang akan dikirim & diset ke akun"
                                    value={notifDefaultPassword}
                                    onChange={(e) => setNotifDefaultPassword(e.target.value)}
                                    className="h-11 rounded-xl bg-slate-50 dark:bg-slate-900/50 border-slate-200 dark:border-slate-800 focus-visible:ring-indigo-500"
                                />
                                <p className="text-xs text-amber-600 dark:text-amber-500 flex items-start gap-1.5">
                                    <KeyRound className="w-3.5 h-3.5 shrink-0 mt-0.5" />
                                    Password akun penerima akan DIRESET ke nilai ini, lalu dikirim via email.
                                </p>
                            </div>
                        )}

                        {/* Pilih User Tertentu (Opsional) */}
                        {notifRoleId && (
                            <div className="space-y-2">
                                <label className="text-sm font-semibold text-slate-700 dark:text-slate-300">
                                    Kirim ke Pengguna Tertentu (Opsional)
                                </label>
                                <p className="text-xs text-slate-500 dark:text-slate-400">
                                    Kosongkan jika ingin mengirim ke semua pengguna dengan role terpilih (sesuai filter target di bawah).
                                </p>
                                <Input
                                    type="text"
                                    placeholder="Cari nama / email..."
                                    value={notifUserSearch}
                                    onChange={(e) => setNotifUserSearch(e.target.value)}
                                    className="h-10 rounded-xl bg-slate-50 dark:bg-slate-900/50 border-slate-200 dark:border-slate-800 focus-visible:ring-indigo-500"
                                />
                                <div className="max-h-40 overflow-y-auto border border-slate-200 dark:border-slate-800 rounded-xl divide-y divide-slate-100 dark:divide-slate-800">
                                    {notifRoleUsersFiltered.length === 0 ? (
                                        <p className="text-sm text-slate-400 p-4 text-center">Tidak ada pengguna dengan role ini.</p>
                                    ) : (
                                        notifRoleUsersFiltered.map((u) => (
                                            <label key={u.user_id} className="flex items-center gap-3 p-2.5 hover:bg-slate-50 dark:hover:bg-slate-800/50 cursor-pointer">
                                                <Checkbox
                                                    checked={notifSelectedUserIds.includes(u.user_id)}
                                                    onCheckedChange={(checked) => {
                                                        if (checked) {
                                                            setNotifSelectedUserIds([...notifSelectedUserIds, u.user_id]);
                                                        } else {
                                                            setNotifSelectedUserIds(notifSelectedUserIds.filter((id) => id !== u.user_id));
                                                        }
                                                    }}
                                                    className="data-[state=checked]:bg-indigo-600 data-[state=checked]:border-indigo-600"
                                                />
                                                <span className="text-sm text-slate-700 dark:text-slate-300 truncate">
                                                    {u.name} <span className="text-slate-400">({u.email})</span>
                                                </span>
                                            </label>
                                        ))
                                    )}
                                </div>
                                {notifSelectedUserIds.length > 0 && (
                                    <p className="text-xs text-indigo-600 dark:text-indigo-400 font-semibold">
                                        {notifSelectedUserIds.length} pengguna dipilih.
                                    </p>
                                )}
                            </div>
                        )}

                        {/* Target Filter */}
                        <div className="space-y-2">
                            <label className="text-sm font-semibold text-slate-700 dark:text-slate-300">Target Pengiriman</label>
                            <Select value={notifFilter} onValueChange={setNotifFilter}>
                                <SelectTrigger className="h-11 rounded-xl bg-slate-50 dark:bg-slate-900/50 border-slate-200 dark:border-slate-800 focus:ring-indigo-500">
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent className="rounded-xl border-slate-200 dark:border-slate-800 shadow-xl">
                                    <SelectItem value="never_sent">Belum Pernah Dikirimi</SelectItem>
                                    <SelectItem value="all">Semua (Sudah / Belum Pernah)</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>

                        {/* Preview jumlah target */}
                        {notifRoleId && (
                            <div className="rounded-xl bg-indigo-50 dark:bg-indigo-900/20 border border-indigo-100 dark:border-indigo-900/50 p-3 text-sm text-indigo-700 dark:text-indigo-400 font-semibold">
                                {notifSelectedUserIds.length > 0 ? (
                                    <>Akan dikirim ke {notifSelectedUserIds.length} pengguna terpilih.</>
                                ) : eligibleCount.isLoading ? (
                                    <span className="flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Menghitung jumlah penerima...</span>
                                ) : (
                                    <>Akan dikirim ke {eligibleCount.data ?? 0} pengguna.</>
                                )}
                            </div>
                        )}

                        {/* Hasil Pengiriman */}
                        {notifResult && (
                            <div className="rounded-xl bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-100 dark:border-emerald-900/50 p-4 text-sm text-emerald-700 dark:text-emerald-400 space-y-1">
                                <p className="font-bold flex items-center gap-2"><MailCheck className="w-4 h-4" /> Pengiriman Selesai</p>
                                <p>Total target: {notifResult.total_targeted} | Berhasil: {notifResult.sent} | Gagal: {notifResult.failed}</p>
                            </div>
                        )}
                    </div>

                    <div className="flex p-6 pt-4 border-t justify-end gap-2 border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-900/50 shrink-0">
                        <Button type="button" variant="outline" onClick={() => setIsSendNotifOpen(false)} className="rounded-xl border-slate-300 dark:border-slate-700">
                            Tutup
                        </Button>
                        <Button
                            type="button"
                            onClick={handleSubmitSendNotif}
                            disabled={sendAccountNotification.isPending}
                            className="rounded-xl bg-indigo-600 hover:bg-indigo-700 text-white shadow-lg"
                        >
                            {sendAccountNotification.isPending ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
                            Kirim
                        </Button>
                    </div>
                </DialogContent>
            </Dialog>

        </div>
    );
}